// Bridges window.till.* in the PWA so Till adapters work via fetch instead of
// IPC. Till consumers see the same surface; under the hood the methods POST
// to the API directly. Hardware namespaces (printer, labelPrinter, power) are
// shimmed to safe no-ops — the PWA renders an on-screen receipt instead of
// driving thermal hardware.
import { api } from "./api";
import { useAuthStore } from "./authStore";
import { useAuthStore as useTillAuthStore } from "@till/lib/stores/authStore";

interface ApiProduct {
  id: string;
  sku: string;
  barcode: string | null;
  name: string;
  sellPrice: string | number;
  taxRate?: string | number;
  unitOfMeasure?: string;
  trackStock?: boolean;
  isActive?: boolean;
  updatedAt?: string;
  totalStock?: number | string;
  imageUrl?: string | null;
  categoryId?: string | null;
}

interface CachedProductLike {
  id: string;
  sku: string;
  barcode: string | null;
  name: string;
  sellPrice: string;
  taxRate: string;
  unit: string;
  trackStock: boolean;
  isActive: boolean;
  updatedAt: string;
  stock: string | null;
  imageUrl: string | null;
  categoryId: string | null;
}

function toCachedProduct(p: ApiProduct): CachedProductLike {
  return {
    id: p.id,
    sku: p.sku,
    barcode: p.barcode ?? null,
    name: p.name,
    sellPrice: String(p.sellPrice ?? "0"),
    taxRate: String(p.taxRate ?? "0"),
    unit: p.unitOfMeasure ?? "",
    trackStock: p.trackStock !== false,
    isActive: p.isActive !== false,
    updatedAt: p.updatedAt ?? new Date().toISOString(),
    stock: p.totalStock != null ? String(p.totalStock) : null,
    imageUrl: p.imageUrl || null,
    categoryId: p.categoryId ?? null,
  };
}

interface ApiUnit {
  id?: string;
  unitId?: string;
  productId: string;
  unitName: string;
  ratioToBase: string | number;
  sellPrice: string | number | null;
  barcode: string | null;
  isBase?: boolean;
  isDefault?: boolean;
  updatedAt?: string;
}

interface CachedUnitLike {
  id: string;
  productId: string;
  unitName: string;
  ratioToBase: string;
  sellPrice: string | null;
  barcode: string | null;
  isDefault: boolean;
  updatedAt: string;
}

function toCachedUnit(u: ApiUnit): CachedUnitLike {
  return {
    id: u.id ?? u.unitId ?? "",
    productId: u.productId,
    unitName: u.unitName,
    ratioToBase: String(u.ratioToBase),
    sellPrice: u.sellPrice == null ? null : String(u.sellPrice),
    barcode: u.barcode ?? null,
    isDefault: !!(u.isDefault ?? u.isBase),
    updatedAt: u.updatedAt ?? new Date().toISOString(),
  };
}

interface ApiCustomer {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  storeCredit?: string | number;
  currentPoints?: number;
  memberLevelId?: string | null;
  isActive?: boolean;
  updatedAt?: string;
}

interface CachedCustomerLike {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  storeCredit: string;
  loyaltyPoints: number;
  memberLevelId: string | null;
  isActive: boolean;
  updatedAt: string;
}

function toCachedCustomer(c: ApiCustomer): CachedCustomerLike {
  return {
    id: c.id,
    name: c.name,
    phone: c.phone ?? null,
    email: c.email ?? null,
    storeCredit: String(c.storeCredit ?? "0"),
    loyaltyPoints: c.currentPoints ?? 0,
    memberLevelId: c.memberLevelId ?? null,
    isActive: c.isActive !== false,
    updatedAt: c.updatedAt ?? new Date().toISOString(),
  };
}

// Mirror Till's *Connected wrapper pattern (ExpensesConnected.tsx,
// QuotationsConnected.tsx): if the user has no storeId (e.g. an unscoped
// admin/manager), fall back to the first active store from /stores. Without
// this the PWA throws "Ask an admin to assign a store" even when exactly one
// store exists and a single-store deployment should just work.
type StoreLite = { id: string; isActive?: boolean };
let storesCachePromise: Promise<StoreLite[]> | null = null;

function loadStoresCached(): Promise<StoreLite[]> {
  if (!storesCachePromise) {
    storesCachePromise = api.get<StoreLite[]>("/stores").catch((err) => {
      storesCachePromise = null;
      throw err;
    });
  }
  return storesCachePromise;
}

export function invalidateStoresCache(): void {
  storesCachePromise = null;
}

async function requireStoreId(): Promise<string> {
  const user = useAuthStore.getState().user;
  if (user?.storeId) return user.storeId;

  const stores = await loadStoresCached();
  const fallback = stores.find((s) => s.isActive !== false) ?? stores[0];
  if (!fallback) {
    throw new Error(
      "This account is not assigned to a store, and no active store exists. Create a store first.",
    );
  }
  return fallback.id;
}

// ---------------------------------------------------------------------------
// Credit-snapshot cache. Without this, every credit-modal open paid a full
// network round-trip to /credit/customer/:id/balance, and re-renders of
// PwaPos triggered duplicate concurrent requests. The Till has the same
// pattern via SQLite (1.5s race against cache); the PWA gets it in-memory.
// ---------------------------------------------------------------------------
type CreditSnapshotShape = {
  existingTab: number;
  creditLimit: number | null;
  source: "live" | "cache";
  fetchedAt: string;
};
const FRESH_TTL_MS = 30_000;          // within this window, return cache without re-fetching
const STALE_TTL_MS = 5 * 60_000;      // beyond fresh but within stale, return cache and refresh under
const RACE_TIMEOUT_MS = 1500;         // bound the worst-case wait when there is no cache
const creditCache = new Map<string, { value: CreditSnapshotShape; fetchedAt: number }>();
const creditInFlight = new Map<string, Promise<CreditSnapshotShape>>();

function fetchCreditLive(customerId: string): Promise<CreditSnapshotShape> {
  let inFlight = creditInFlight.get(customerId);
  if (!inFlight) {
    inFlight = (async () => {
      try {
        const r = await api.get<{ existingTab?: number; balance?: number; creditLimit?: number | null }>(
          `/credit/customer/${customerId}/balance`,
        );
        const rawBalance = Number(r.balance ?? 0) || 0;
        const snap: CreditSnapshotShape = {
          existingTab: r.existingTab ?? (rawBalance < 0 ? Math.abs(rawBalance) : 0),
          creditLimit: r.creditLimit ?? null,
          source: "live",
          fetchedAt: new Date().toISOString(),
        };
        creditCache.set(customerId, { value: snap, fetchedAt: Date.now() });
        return snap;
      } catch {
        // Network failure: don't poison the cache. Return a zero snapshot so the
        // modal renders something instead of hanging on null forever.
        return {
          existingTab: 0,
          creditLimit: null,
          source: "live",
          fetchedAt: new Date().toISOString(),
        };
      } finally {
        creditInFlight.delete(customerId);
      }
    })();
    creditInFlight.set(customerId, inFlight);
  }
  return inFlight;
}

async function getCreditSnapshotCached(customerId: string): Promise<CreditSnapshotShape> {
  const now = Date.now();
  const cached = creditCache.get(customerId);

  // 1. Fresh cache → return immediately.
  if (cached && now - cached.fetchedAt < FRESH_TTL_MS) {
    return { ...cached.value, source: "cache" };
  }

  const live = fetchCreditLive(customerId);

  // 2. Stale-but-recent cache → return it now and let `live` refresh underneath.
  if (cached && now - cached.fetchedAt < STALE_TTL_MS) {
    void live;
    return { ...cached.value, source: "cache" };
  }

  // 3. No cache or very old → race the live fetch against a hard timeout so a
  //    slow link can't stall the modal indefinitely. If timeout wins and we
  //    have any prior cache (even very old), return that; otherwise wait.
  return Promise.race([
    live,
    new Promise<CreditSnapshotShape>((resolve) => {
      setTimeout(() => {
        if (cached) resolve({ ...cached.value, source: "cache" });
        else resolve(live); // give up the race; let the live promise resolve naturally
      }, RACE_TIMEOUT_MS);
    }),
  ]);
}

/** Drop a cached credit snapshot — call after writes that change the balance. */
export function invalidateCreditCache(customerId?: string): void {
  if (customerId) creditCache.delete(customerId);
  else creditCache.clear();
}

// Mirror the PWA's auth state into the Till's authStore. The Till's adapters
// (useShiftAdapter, etc.) read can()/user from that store; without this sync
// the Till store stays empty in the PWA and adapters see the user as a
// permission-less anonymous — which makes useShiftAdapter render the
// "Manager PIN" override field for *managers* and show cashier name as "—".
function syncTillAuthFromPwa(): void {
  const u = useAuthStore.getState().user;
  useTillAuthStore.setState({
    user: u
      ? {
          userId: u.id,
          name: u.name ?? null,
          role: u.role,
          storeId: u.storeId,
          deviceId: null,
          permissions: u.permissions ?? [],
        }
      : null,
    loading: false,
    error: null,
  });
}

export function installTillShim(): void {
  syncTillAuthFromPwa();
  useAuthStore.subscribe(syncTillAuthFromPwa);

  const shim = {
    // Marker the shared TillSettingsConnected reads to render a "Hardware
    // controls live in the desktop app" banner in the PWA. Electron's preload
    // does not set this, so a falsy check identifies the desktop build.
    __isPwa: true,

    api,

    auth: {
      login: async () => {
        throw new Error("Use the PWA login form, not window.till.auth.login.");
      },
      logout: async () => true,
      me: async () => {
        const u = useAuthStore.getState().user;
        if (!u) return null;
        // Reshape to Till's CurrentUser (userId, deviceId fields).
        return {
          userId: u.id,
          name: u.name ?? null,
          role: u.role,
          storeId: u.storeId,
          deviceId: null,
          permissions: u.permissions ?? [],
          mustChangePassword: u.mustChangePassword ?? false,
        };
      },
      // Forced-password-change: the shared ChangePassword page calls this via
      // window.till.api.auth.changePassword. In the PWA it POSTs through the
      // same authed fetch client as every other call.
      changePassword: async (currentPassword: string, newPassword: string) =>
        api.changePassword(currentPassword, newPassword),
    },

    settings: {
      get: async () => ({
        apiBaseUrl: "",
        storeId: useAuthStore.getState().user?.storeId ?? null,
        deviceId: null,
        lastSyncAt: null,
        theme: "light" as const,
      }),
      set: async () => true,
      setApiUrl: async () => true,
    },

    catalog: {
      search: async (
        term: string,
        opts?: { categoryId?: string | null },
      ): Promise<CachedProductLike[]> => {
        const q = (term ?? "").trim();
        // The legacy non-paginated /products endpoint reads `search` (not `q`)
        // and caps `limit` at 500. Using `q` or `pageSize` is silently rejected.
        // categoryId is supported on the same path for client-side filtering;
        // if the endpoint ignores it the PWA falls back to filtering in JS.
        const params: string[] = ['limit=500'];
        if (q.length) params.push(`search=${encodeURIComponent(q)}`);
        if (opts?.categoryId) params.push(`categoryId=${encodeURIComponent(opts.categoryId)}`);
        const path = `/products?${params.join('&')}`;
        try {
          const result = await api.get<ApiProduct[] | { rows: ApiProduct[] }>(path);
          const rows = Array.isArray(result) ? result : (result.rows ?? []);
          // Defensive fallback in case the endpoint doesn't filter by categoryId.
          const filtered = opts?.categoryId
            ? rows.filter((r: any) => r.categoryId === opts.categoryId)
            : rows;
          return filtered.map(toCachedProduct);
        } catch (e) {
          console.error("[tillShim] catalog.search failed", e);
          return [];
        }
      },

      categories: async () => {
        try {
          const rows = await api.get<Array<{
            id: string;
            name: string;
            parentId?: string | null;
            sortOrder?: string | number | null;
            createdAt?: string;
            updatedAt?: string;
          }>>("/categories");
          return rows.map((c) => ({
            id: c.id,
            name: c.name,
            parentId: c.parentId ?? null,
            sortOrder: String(c.sortOrder ?? '0'),
            updatedAt: c.updatedAt ?? c.createdAt ?? new Date().toISOString(),
          }));
        } catch (e) {
          console.error("[tillShim] catalog.categories failed", e);
          return [];
        }
      },

      barcode: async (code: string) => {
        // Try unit barcode first (multi-unit SKU like a 50kg sack), then product.
        try {
          const unit = await api.get<ApiUnit | null>(
            `/product-units/by-barcode/${encodeURIComponent(code)}`,
          );
          if (unit) {
            const product = await api.get<ApiProduct>(`/products/${unit.productId}`);
            return {
              kind: "unit" as const,
              product: toCachedProduct(product),
              unit: toCachedUnit(unit),
            };
          }
        } catch {
          /* fall through */
        }
        try {
          const product = await api.get<ApiProduct | null>(
            `/products/barcode/${encodeURIComponent(code)}`,
          );
          if (product) {
            return { kind: "product" as const, product: toCachedProduct(product) };
          }
        } catch {
          /* fall through */
        }
        return null;
      },

      productUnits: async (productId: string): Promise<CachedUnitLike[]> => {
        try {
          const rows = await api.get<ApiUnit[]>(`/product-units/by-product/${productId}`);
          return rows.map(toCachedUnit);
        } catch {
          return [];
        }
      },

      customers: async (term: string): Promise<CachedCustomerLike[]> => {
        const q = (term ?? "").trim();
        if (q.length < 2) return [];
        try {
          const r = await api.get<ApiCustomer[] | { rows: ApiCustomer[] }>(
            `/customers?q=${encodeURIComponent(q)}&pageSize=20`,
          );
          const rows = Array.isArray(r) ? r : (r.rows ?? []);
          return rows.map(toCachedCustomer);
        } catch {
          return [];
        }
      },

      createCustomer: async (dto: { name: string; phone: string; email?: string; notes?: string }) => {
        const storeId = useAuthStore.getState().user?.storeId ?? undefined;
        const created = await api.post<ApiCustomer & { storeId?: string | null }>("/customers", {
          ...dto,
          storeId,
        });
        return {
          id: created.id,
          name: created.name,
          phone: created.phone ?? "",
          storeId: created.storeId ?? storeId ?? "",
        };
      },

      creditSnapshot: async (customerId: string) => {
        return getCreditSnapshotCached(customerId);
      },
    },

    sales: {
      create: async (payload: {
        // unitPriceOverride/discount: price echo from the shared POS page —
        // forwarded verbatim; POST /sales already honours the override field.
        items: Array<{
          productId: string;
          productUnitId?: string;
          quantity: string;
          unitPriceOverride?: string;
          discount?: string;
        }>;
        payments: Array<{ method: string; amount: string }>;
        customerId?: string;
        discountTotal?: string;
        couponCode?: string;
        loyaltyPointsRedeemed?: number;
        storeCreditUsed?: string;
        drawerSessionId?: string;
        heldFromId?: string;
        note?: string;
      }) => {
        const storeId = await requireStoreId();
        const dto = {
          storeId,
          customerId: payload.customerId,
          items: payload.items,
          payments: payload.payments,
          discountTotal: payload.discountTotal,
          couponCode: payload.couponCode,
          loyaltyPointsRedeemed: payload.loyaltyPointsRedeemed,
          drawerSessionId: payload.drawerSessionId,
          heldFromId: payload.heldFromId,
          note: payload.note,
        };
        const result = await api.post<{
          sale: { id: string; receiptNumber?: string | null; createdAt?: string };
          receipt?: { receiptNumber?: string | null; createdAt?: string };
        }>(
          "/sales",
          dto,
        );
        return {
          eventId: result.sale.id,
          receiptNumber: result.sale.receiptNumber ?? result.receipt?.receiptNumber ?? null,
          clientCreatedAt: result.sale.createdAt ?? result.receipt?.createdAt ?? new Date().toISOString(),
        };
      },

      void: async (payload: { saleId: string; reason?: string }) => {
        await api.post(`/sales/${payload.saleId}/void`, { reason: payload.reason });
        return { eventId: payload.saleId };
      },

      recent: async (filter?: { from?: string; to?: string }) => {
        try {
          const query: Record<string, unknown> = { page: 1, pageSize: 100 };
          if (filter?.from) query.from = filter.from;
          if (filter?.to)   query.to   = filter.to;
          const r = await api.get<
            | Array<{
                id: string;
                receiptNumber?: string | null;
                total?: string | number;
                createdAt?: string;
                status?: string;
                paymentSummary?: { method?: string | null; amount?: string | number | null } | null;
                payments?: Array<{
                  method?: string | null;
                  amount?: string | number | null;
                  reference?: string | null;
                }>;
              }>
            | {
                rows: Array<{
                  id: string;
                  receiptNumber?: string | null;
                  total?: string | number;
                  createdAt?: string;
                  status?: string;
                  paymentSummary?: { method?: string | null; amount?: string | number | null } | null;
                  payments?: Array<{
                    method?: string | null;
                    amount?: string | number | null;
                    reference?: string | null;
                  }>;
                }>;
              }
          >("/sales", query);
          const rows = Array.isArray(r) ? r : (r.rows ?? []);
          return rows.map((s) => {
            const salePayments = Array.isArray(s.payments) && s.payments.length > 0
              ? s.payments
                  .filter((p): p is { method: string; amount?: string | number | null; reference?: string | null } => !!p.method)
                  .map((p) => ({
                    method: p.method,
                    amount: Number(p.amount ?? 0) || 0,
                    reference: p.reference ?? undefined,
                  }))
              : s.paymentSummary?.method
                ? [{
                    method: s.paymentSummary.method,
                    amount: Number(s.paymentSummary.amount ?? s.total ?? 0) || 0,
                  }]
                : [];
            return {
              eventId: s.id,
              receiptNumber: s.receiptNumber ?? null,
              total: String(s.total ?? "0"),
              itemsJson: "[]",
              paymentsJson: JSON.stringify(salePayments),
              createdAt: s.createdAt ?? new Date().toISOString(),
              status: (s.status === "conflict_pending" ? "conflict" : "synced") as "pending" | "synced" | "conflict",
            };
          });
        } catch {
          return [];
        }
      },
    },

    stock: {
      adjust: async (payload: {
        productId: string;
        productUnitId?: string;
        quantityDelta: string;
        movementType: string;
        note?: string;
      }) => {
        const storeId = await requireStoreId();
        await api.post("/inventory/adjust", { ...payload, storeId });
        return { eventId: crypto.randomUUID() };
      },
    },

    drawer: {
      open: async (payload: { openingFloat: string; note?: string }) => {
        const storeId = await requireStoreId();
        // API DTO field is `note`, not `openNote`. With forbidNonWhitelisted=true
        // any other key would 400 the request once the user actually types a note.
        const body: { storeId: string; openingCash: string; note?: string } = {
          storeId,
          openingCash: payload.openingFloat,
        };
        if (payload.note) body.note = payload.note;
        const r = await api.post<{ id: string; status: string; openedAt?: string; openingCash?: string }>(
          "/drawer/open",
          body,
        );
        return {
          id: r.id,
          status: r.status,
          source: "server" as const,
          openingFloat: r.openingCash ?? payload.openingFloat,
          openedAt: r.openedAt ?? new Date().toISOString(),
        };
      },

      close: async (payload: { countedAmount: string; note?: string }) => {
        const cur = await api.get<{ id: string } | null>("/drawer/current");
        if (!cur?.id) throw new Error("No open drawer to close.");
        // API DTO fields are `countedCash` + `note`. The previous payload used
        // `countedAmount` + `closeNote`, which the global ValidationPipe rejected
        // (forbidNonWhitelisted) — the 400 surfaced as a silent unhandled
        // rejection in the renderer, so the button looked dead.
        // approveVariance is only honoured server-side when the closing user
        // has money.drawer_approve_variance (managers bypass). Setting it here
        // unblocks managers closing with variance > 10 000 LAK; cashiers still
        // hit the 403 they would have hit anyway. Without this flag, even a
        // manager closing 1 ₭ over threshold gets a 409 ConflictException.
        const body: { countedCash: string; note?: string; approveVariance?: boolean } = {
          countedCash: payload.countedAmount,
          approveVariance: true,
        };
        if (payload.note) body.note = payload.note;
        await api.post(`/drawer/${cur.id}/close`, body);
        return { eventId: cur.id };
      },

      movement: async (payload: { type: string; amount: string; note?: string }) => {
        const cur = await api.get<{ id: string } | null>("/drawer/current");
        if (!cur?.id) throw new Error("No open drawer.");
        await api.post(`/drawer/${cur.id}/movement`, payload);
        return { eventId: cur.id };
      },

      // No physical drawer in the browser; PwaPos hides the Open-Drawer button,
      // so this stub exists only to satisfy the shared till API shape.
      noSale: async () => {
        throw new Error("Cash drawer is only available in the desktop app.");
      },

      activeSessionId: async () => {
        try {
          const r = await api.get<{ id: string } | null>("/drawer/current");
          return r?.id ?? null;
        } catch {
          return null;
        }
      },

      summary: async () => {
        try {
          const cur = await api.get<{
            id: string;
            openingCash?: string | number;
            openedAt?: string;
          } | null>("/drawer/current");
          if (!cur?.id) return null;

          // Authoritative summary needs the full movements ledger, not just
          // the snapshot fields on the session row. /drawer/:id returns
          // {session, movements}; /drawer/:id/tender-split returns the
          // per-method breakdown that the cash-only movements view misses
          // (split tenders, QR-only sales, etc.).
          const [detail, tenderRowsRaw] = await Promise.all([
            api.get<{
              session: { id: string; openingCash?: string | number; openedAt?: string };
              movements: Array<{
                id: string;
                type: string;
                amount: string;
                createdAt: string;
                note: string | null;
              }>;
            }>(`/drawer/${cur.id}`),
            api
              .get<Array<{ method: string; count: number; amount: number }>>(
                `/drawer/${cur.id}/tender-split`,
              )
              .catch(() => [] as Array<{ method: string; count: number; amount: number }>),
          ]);

          const opening = Number(detail.session.openingCash ?? cur.openingCash ?? 0) || 0;
          const openedAt = detail.session.openedAt ?? cur.openedAt ?? new Date().toISOString();

          let cashSales = 0;
          let cashRefunds = 0;
          let cashMovementsIn = 0;
          let cashMovementsOut = 0;
          let expensesOut = 0;

          const cashTimeline: Array<{
            at: string;
            type: "sale" | "expense" | "movement_in" | "movement_out";
            amount: number;
            note?: string;
          }> = [];

          for (const m of detail.movements) {
            const amt = Number(m.amount) || 0;
            switch (m.type) {
              case "sale_cash":
                cashSales += amt;
                cashTimeline.push({ at: m.createdAt, type: "sale", amount: amt, note: m.note ?? undefined });
                break;
              case "sale_refund_cash":
                cashRefunds += Math.abs(amt);
                cashTimeline.push({ at: m.createdAt, type: "movement_out", amount: -Math.abs(amt), note: m.note ?? "Refund" });
                break;
              case "expense_out":
                expensesOut += Math.abs(amt);
                cashTimeline.push({ at: m.createdAt, type: "expense", amount: -Math.abs(amt), note: m.note ?? undefined });
                break;
              case "cash_in":
              case "safe_float_in":
              case "customer_payment_cash":
                cashMovementsIn += amt;
                cashTimeline.push({ at: m.createdAt, type: "movement_in", amount: amt, note: m.note ?? undefined });
                break;
              case "cash_out":
              case "bank_deposit":
              case "supplier_payment_cash":
                cashMovementsOut += Math.abs(amt);
                cashTimeline.push({ at: m.createdAt, type: "movement_out", amount: -Math.abs(amt), note: m.note ?? undefined });
                break;
            }
          }

          // Fall back to a synthesised cash row if the tender-split call failed
          // (e.g. older API without the endpoint).
          const tenderSplit = tenderRowsRaw.length > 0
            ? tenderRowsRaw.map((r) => ({ method: r.method, amount: Number(r.amount) || 0, count: Number(r.count) || 0 }))
            : (() => {
                const cashCount = detail.movements.filter((m) => m.type === "sale_cash").length;
                return cashCount > 0 ? [{ method: "cash", amount: cashSales, count: cashCount }] : [];
              })();

          return {
            sessionId: cur.id,
            source: "server" as const,
            openedAt,
            openingFloat: opening,
            cashSales,
            cashRefunds,
            cashMovementsIn,
            cashMovementsOut,
            expensesOut,
            expected: opening + cashSales - cashRefunds + cashMovementsIn - cashMovementsOut - expensesOut,
            tenderSplit,
            cashTimeline,
          };
        } catch (e) {
          // Don't swallow real failures — a 403/500 on /drawer/:id used to
          // return null here, wedging the Shift page on an eternal
          // "Loading shift…" with no explanation. "No open session" is the
          // only null; everything else surfaces to drawerStore.
          throw e instanceof Error ? e : new Error(String(e));
        }
      },
    },

    expense: {
      create: async (payload: {
        categoryId: string;
        amount: string;
        paidFromCashDrawer: boolean;
        description?: string;
        receiptImageUrl?: string;
      }) => {
        const storeId = await requireStoreId();
        await api.post("/expenses", { storeId, ...payload });
        return { eventId: crypto.randomUUID() };
      },
    },

    heldOrders: {
      save: async (payload: { label?: string; cart: unknown; note?: string }) => {
        const storeId = await requireStoreId();
        // /held-orders DTO: storeId UUID, name string (required, ≤120), cart
        // object. `note` isn't in the DTO so global ValidationPipe with
        // forbidNonWhitelisted would reject it — fold it into the cart blob.
        const cart = (payload.cart && typeof payload.cart === "object"
          ? { ...(payload.cart as Record<string, unknown>) }
          : { value: payload.cart }) as Record<string, unknown>;
        if (payload.note) cart.note = payload.note;
        const name = (payload.label && payload.label.trim()) ||
          `Held ${new Date().toLocaleString()}`;
        const r = await api.post<{ id: string }>("/held-orders", {
          storeId,
          name: name.slice(0, 120),
          cart,
        });
        return { id: r.id };
      },

      list: async () => {
        try {
          const rows = await api.get<
            Array<{
              id: string;
              name?: string | null;
              // Server returns the JSONB column already deserialized to an
              // object; the renderer's adapter then JSON.parse()s it, so we
              // re-stringify here. Skipping this leaves every held order
              // showing 0 items / 0 ₭ in the picker.
              cartJson?: unknown;
              note?: string | null;
              // Server's column is `held_at` → drizzle camelCases to `heldAt`.
              // The renderer's HeldOrder type expects `createdAt`.
              heldAt?: string;
              status?: string;
            }>
          >("/held-orders");
          return rows.map((h) => ({
            id: h.id,
            label: h.name ?? null,
            cartJson:
              typeof h.cartJson === "string"
                ? h.cartJson
                : JSON.stringify(h.cartJson ?? {}),
            note: h.note ?? null,
            createdAt: h.heldAt ?? new Date().toISOString(),
            status: (h.status === "held" ? "active" : h.status ?? "active") as
              | "active"
              | "resumed"
              | "cancelled",
            serverId: h.id,
          }));
        } catch {
          return [];
        }
      },

      resume: async (id: string) => {
        try {
          const r = await api.post<{
            id: string;
            cartJson?: unknown;
            note?: string | null;
            heldAt?: string;
          }>(`/held-orders/${id}/resume`);
          return {
            id: r.id,
            label: null,
            cartJson:
              typeof r.cartJson === "string"
                ? r.cartJson
                : JSON.stringify(r.cartJson ?? {}),
            note: r.note ?? null,
            createdAt: r.heldAt ?? new Date().toISOString(),
            status: "resumed" as const,
            serverId: r.id,
          };
        } catch {
          return null;
        }
      },

      cancel: async (id: string) => {
        try {
          await api.delete(`/held-orders/${id}`);
          return true;
        } catch {
          return false;
        }
      },
    },

    sync: {
      state: async () => ({
        online: navigator.onLine,
        pending: 0,
        lastSyncAt: null,
        lastError: null,
      }),
      trigger: async () => ({
        online: navigator.onLine,
        pending: 0,
        lastSyncAt: null,
        lastError: null,
      }),
      onState: () => () => {},
    },

    printer: {
      // No Windows printers in the browser — the Settings dropdown stays empty
      // and the page shows its "install in Windows first" hint.
      listPrinters: async () => [],
      getConfig: async () => ({ enabled: false, transport: "none" as const }),
      setConfig: async (cfg: unknown) => cfg as never,
      // Hardware tests throw so the TillSettings pill flips to "error" instead
      // of falsely reporting "OK". Cash-sale callers (PosWithScanner) wrap
      // print/kickDrawer in try/catch and swallow, so a throw is harmless there.
      test: async () => {
        throw new Error("Receipt printer is only available in the desktop app.");
      },
      print: async () => ({ printed: false, reason: "PWA renders receipts on-screen" }),
      kickDrawer: async () => {
        throw new Error("Cash drawer is only available in the desktop app.");
      },
    },

    labelPrinter: {
      getConfig: async () => ({ enabled: false, transport: "none" as const }),
      setConfig: async (cfg: unknown) => cfg as never,
      test: async () => {
        throw new Error("Label printer is only available in the desktop app.");
      },
      print: async () => ({ printed: false, reason: "PWA has no label printer" }),
      priceTag: async () => ({ printed: false, reason: "PWA has no label printer" }),
    },

    power: {
      status: async () => ({
        onAc: true,
        batteryPercent: null,
        chargeStatus: "unknown" as const,
        timestamp: new Date().toISOString(),
      }),
      onStatus: () => () => {},
      onWarning: () => () => {},
      onCritical: () => () => {},
    },

    // Sync Queue is the Till's offline event queue (SQLite). The PWA is
    // online-only (per Golden Rule 15), so it has no queue to inspect.
    // The shared SyncQueue page mounts the same adapter on both apps;
    // these stubs throw so the page surfaces a clear "Till desktop only"
    // banner instead of silently rendering empty.
    queue: {
      list: async () => {
        throw new Error("Sync Queue is only available in the Till desktop app (the PWA has no offline queue).");
      },
      stats: async () => {
        throw new Error("Sync Queue is only available in the Till desktop app (the PWA has no offline queue).");
      },
      retry: async () => {
        throw new Error("Retry is only available in the Till desktop app.");
      },
      discard: async () => {
        throw new Error("Discard is only available in the Till desktop app.");
      },
      bulkDiscard: async () => {
        throw new Error("Bulk discard is only available in the Till desktop app.");
      },
      clearSynced: async () => {
        throw new Error("Clear synced is only available in the Till desktop app.");
      },
    },
  };

  (window as unknown as { till: unknown }).till = shim;
}
